// Reine Baum-Logik für FolderTree (WAI-ARIA Tree): sichtbare Zeilen und Tastaturbewegung.

export interface TreeNodeLike {
  id: string;
  name: string;
  children?: TreeNodeLike[];
}

export interface VisibleRow<T extends TreeNodeLike> {
  node: T;
  level: number;
  parentId: string | null;
  hasChildren: boolean;
  expanded: boolean;
}

/** Zeilen in Anzeige-Reihenfolge; Kinder nur unter aufgeklappten Knoten. */
export function visibleRows<T extends TreeNodeLike>(
  nodes: T[],
  expanded: ReadonlySet<string>,
  level = 1,
  parentId: string | null = null,
): VisibleRow<T>[] {
  const rows: VisibleRow<T>[] = [];
  for (const node of nodes) {
    const children = (node.children ?? []) as T[];
    const hasChildren = children.length > 0;
    const isOpen = hasChildren && expanded.has(node.id);
    rows.push({ node, level, parentId, hasChildren, expanded: isOpen });
    if (isOpen) rows.push(...visibleRows(children, expanded, level + 1, node.id));
  }
  return rows;
}

/** Alle Knoten in Baum-Reihenfolge mit Ebene (mobile Auswahlliste). */
export function allRows<T extends TreeNodeLike>(
  nodes: T[],
  level = 1,
): { node: T; level: number }[] {
  return nodes.flatMap((node) => [
    { node, level },
    ...allRows((node.children ?? []) as T[], level + 1),
  ]);
}

export type TreeAction =
  | { type: 'focus'; id: string }
  | { type: 'expand'; id: string }
  | { type: 'collapse'; id: string }
  | { type: 'select'; id: string };

/**
 * Ergebnis einer Taste auf der fokussierten Zeile (WAI-ARIA Tree Pattern): ↑/↓, ← (zuklappen bzw.
 * zum Elternknoten), → (aufklappen bzw. zum ersten Kind), Home/End, Enter/Leertaste wählt,
 * Buchstaben springen zur nächsten Zeile mit diesem Anfang (Typeahead).
 */
export function treeKey<T extends TreeNodeLike>(
  rows: VisibleRow<T>[],
  focusedId: string,
  key: string,
): TreeAction | null {
  const index = rows.findIndex((row) => row.node.id === focusedId);
  if (index < 0) return rows[0] ? { type: 'focus', id: rows[0].node.id } : null;
  const row = rows[index]!;
  switch (key) {
    case 'ArrowDown':
      return rows[index + 1] ? { type: 'focus', id: rows[index + 1]!.node.id } : null;
    case 'ArrowUp':
      return rows[index - 1] ? { type: 'focus', id: rows[index - 1]!.node.id } : null;
    case 'Home':
      return { type: 'focus', id: rows[0]!.node.id };
    case 'End':
      return { type: 'focus', id: rows[rows.length - 1]!.node.id };
    case 'ArrowRight':
      if (row.hasChildren && !row.expanded) return { type: 'expand', id: row.node.id };
      if (row.expanded && rows[index + 1]) return { type: 'focus', id: rows[index + 1]!.node.id };
      return null;
    case 'ArrowLeft':
      if (row.expanded) return { type: 'collapse', id: row.node.id };
      return row.parentId ? { type: 'focus', id: row.parentId } : null;
    case 'Enter':
    case ' ':
      return { type: 'select', id: row.node.id };
    default: {
      if (key.length !== 1 || !/\S/.test(key)) return null;
      const lower = key.toLowerCase();
      for (let step = 1; step <= rows.length; step += 1) {
        const candidate = rows[(index + step) % rows.length]!;
        if (candidate.node.name.toLowerCase().startsWith(lower))
          return { type: 'focus', id: candidate.node.id };
      }
      return null;
    }
  }
}
